import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnProps,
  GridNoRecords,
  GridPageChangeEvent,
  GridSortChangeEvent,
} from "@progress/kendo-react-grid";
import { Loader } from "@progress/kendo-react-indicators";
import React, { Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CustomUserGridInfo from "../../../components/custom/grid/CustomUserGridInfo";
import useLocale from "../../../hooks/useLocale";
import usersService from "../../../services/users.service";
import { Dictionary } from "../../../types/Dictionary";
import {
  InsightMetricCell,
  InsightMetricKeyLabel,
  InsightViewByTblData,
} from "../../../types/insight";
import { TinyUser } from "../../../types/user";
import {
  concatUsername,
  getRandomProfileColor,
} from "../../../utils/profileUtils";
import useAuth from "../../../hooks/useAuth";
import useSwal from "../../../hooks/useSwal";
import { SortDescriptor } from "@progress/kendo-data-query";
//import InsightViewByGridForMobile from "./InsightViewByGridForMobile";
import useTranslation from "../../../hooks/useTranslation";
import {
  InsightKeysData,
  insightKeys,
  processMetricKeyForDisplay,
} from "../InsightTranslationKeys";
import { GetTinyUsersDto } from "../../../types/user/GetTinyUsersDto";
import { ErrorBoundary } from "react-error-boundary";
import fallbackRender from "../../../components/ErrorBoundary";
const InsightViewByGridForMobile = React.lazy(()=>import("./InsightViewByGridForMobile"));
interface UsersInsightMetricCell {
  columnName: string;
  metricKeyLabel: string;
  metricKeyCounts: { metricKey: TinyUser; count: number }[];
}

export interface AggregateData {
  name: string;
  additionalProperties: Dictionary<string>;
  displayName: string;
  subKeyCallsCnt: { key: string; count: number }[];
  totalCallsCnt: number;
  sentiments?: InsightMetricCell;
  topTopics?: InsightMetricCell;
  topMoments?: InsightMetricCell;
  topUsers?: UsersInsightMetricCell;
  evaluation?: InsightMetricCell;
  // Added for Evaluators
  evaluators?: UsersInsightMetricCell;
  duration?: string;
  averageScore?: string;
}

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

interface InsightViewByGridProps {
  viewByFilterTblData?: InsightViewByTblData;
  viewBy: InsightMetricKeyLabel;
  error?: string;
  onViewByCallCountClickHandler: (
    metricKeyLabel: string,
    metricKey: string,
    metricSubKeyLabel?: string,
    displayName?: string
  ) => void;
}

export interface CustomGridCellProps extends GridCellProps {
  dataItem: AggregateData;
}

const InsightViewByGrid: React.FC<InsightViewByGridProps> = ({
  viewByFilterTblData,
  viewBy,
  error,
  onViewByCallCountClickHandler,
}) => {
  const trans = useTranslation("InsightsViewByGrid");
  const localeCtx = useLocale();
  const auth = useAuth();
  const swal = useSwal();

  const CustomTopUsersPropCell = (props: CustomGridCellProps) => {
    const userMetrics = props.dataItem.topUsers;

    const UserBadge: React.FC<{ userInfo: TinyUser; cnt: number }> = ({
      userInfo,
      cnt,
    }) => {
      const name = concatUsername(
        userInfo?.firstName ? userInfo.firstName : "",
        userInfo?.lastName ? userInfo?.lastName : ""
      ).trim();
      const color = getRandomProfileColor(userInfo?.id ?? 0);
      const userIcon = userInfo?.image ? (
        <div className="contact-list-icon-img">
          <img src={require("../../../assets/images/user-img-2.jpg")} />
        </div>
      ) : name !== "" ? (
        <span>
          {name
            .split(" ")
            .map((n: string) => n[0])
            .join("")}
        </span>
      ) : (
        <span className="fs-10">UU</span>
      );

      return (
        <>
          {!userInfo ? (
            <span></span>
          ) : (
            <div
              className="keywordTrk"
              style={{
                border: "2px solid rgba(0,0,0,.08)",
                background: "transparent",
              }}
            >
              <div className="tblUsr float-left">
                <div className="topUsrAreaPic">
                  <div
                    className="topUsrAreaPic-i"
                    style={{ padding: "2px 1px 2px 0" }}
                  >
                    <div
                      className={`contact-list-icon ${color}`}
                      title={`${name}`}
                      style={{ height: "22px", width: "22px", border: "none" }}
                    >
                      <div className="contact-list-icon-txt fs-10">
                        {userIcon}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <span
                className="numCount"
                title={trans.fetchLabelKeyTranslation("TextCalls", "Calls")}
                style={{
                  color: "rgba(0,0,0,.4)",
                  padding: "0",
                  cursor: "default",
                }}
              >
                {cnt}
              </span>
            </div>
          )}
        </>
      );
    };

    return (
      <td className="mx-th-tag mxLabel">
        <div className="mx-td-spn">
          <div className="keywordRow">
            {userMetrics &&
              userMetrics.metricKeyCounts.map((m, idx) => {
                return (
                  <UserBadge key={idx} userInfo={m.metricKey} cnt={m.count} />
                );
              })}
          </div>
        </div>
      </td>
    );
  };

  const CustomTopTopicsPropCell = (props: CustomGridCellProps) => {
    const topicMetrics = props.dataItem.topTopics;
    return (
      <td className="mx-th-tag">
        <div className="mx-td-spn">
          <div className="keywordRow">
            {topicMetrics &&
              topicMetrics.metricKeyCounts.map((m, idx) => {
                return (
                  <div
                    className={`keywordTrk ${
                      m.additionalProperties["IsExactMatch"] === "true"
                        ? "bg-greenDarkDLL"
                        : ""
                    }`}
                    key={idx}
                  >
                    <i
                      className={`bi ${
                        m.metricKey.substring(0, 3) === "PUB"
                          ? "bi-people"
                          : "bi-lock-fill"
                      } m-r-6`}
                    ></i>
                    {m.metricKey.substring(4)}

                    {m.subKeyCounts.map((c) => {
                      return (
                        <span
                          style={{ cursor: "default" }}
                          className={`numCount`}
                          title={trans.fetchLabelKeyTranslation(
                            "TextCalls",
                            "Calls"
                          )}
                        >
                          {c.count}
                        </span>
                      );
                    })}
                  </div>
                );
              })}
          </div>
        </div>
      </td>
    );
  };

  const CustomTopMomentsPropCell = (props: CustomGridCellProps) => {
    const momentMetrics = props.dataItem.topMoments;
    return (
      <td className="mx-th-tag">
        <div className="mx-td-spn">
          <div className="keywordRow">
            {momentMetrics &&
              momentMetrics.metricKeyCounts.map((m, idx) => {
                return (
                  <div className="keywordTrk" key={idx}>
                    <i
                      className={`bi ${
                        m.metricKey.substring(0, 3) === "PUB"
                          ? "bi-people"
                          : "bi-lock-fill"
                      } m-r-6`}
                    ></i>
                    {m.metricKey.substring(4)}
                    <span
                      className="numCount"
                      title={trans.fetchLabelKeyTranslation(
                        "TextCalls",
                        "Calls"
                      )}
                    >
                      {m.subKeyCounts.map((c) => {
                        return <span>{c.count}</span>;
                      })}
                    </span>
                  </div>
                );
              })}
          </div>
        </div>
      </td>
    );
  };

  const CustomSentimentsCell = (props: CustomGridCellProps) => {
    const sentimentMetrics = props.dataItem.sentiments;

    const SentimentBadge: React.FC<{ sentiment: string; cnt: number }> = ({
      sentiment,
      cnt,
    }) => {
      const cls =
        sentiment === "Positive"
          ? "success"
          : sentiment === "Negative"
          ? "danger"
          : "warning";
      const emoji =
        sentiment === "Positive"
          ? "smile"
          : sentiment === "Negative"
          ? "frown"
          : "neutral";
      return (
        <div
          className="keywordTrk"
          style={{
            border: "2px solid rgba(0,0,0,.08)",
            background: "transparent",
          }}
        >
          <div className="tblUsr float-left">
            <div className="topUsrAreaPic">
              <div
                className="topUsrAreaPic-i"
                style={{ padding: "3px 1px 2px 0" }}
              >
                <div
                  className="contact-list-icon"
                  title={sentiment}
                  style={{ height: "22px", width: "22px", border: "none" }}
                >
                  <i
                    className={`bi bi-emoji-${emoji}-fill fs-20 text-${cls}`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
          <span
            className="numCount"
            title={trans.fetchLabelKeyTranslation("TextCalls", "Calls")}
            style={{ color: "rgba(0,0,0,.4)", padding: "0", cursor: "default" }}
          >
            {cnt}
          </span>
        </div>
      );
    };

    return (
      <td className="mx-th-tag mxLabel">
        <div className="mx-td-spn">
          <div className="keywordRow">
            {sentimentMetrics &&
              sentimentMetrics.metricKeyCounts.map((m, idx) => {
                return (
                  <SentimentBadge
                    key={idx}
                    sentiment={m.metricKey}
                    cnt={m.totalCount}
                  />
                );
              })}
          </div>
        </div>
      </td>
    );
  };

  const CustomViewByHeaderCell = (props: CustomGridCellProps) => {
    const data = props.dataItem;
    let tinyUser: TinyUser | undefined = undefined;
    if (viewBy.name === "Users" || viewBy.name === "Evaluators") {
      try {
        tinyUser = JSON.parse(data.displayName);
      } catch (err) {}
    }

    const label = tinyUser ? (
      <CustomUserGridInfo userInfo={tinyUser} />
    ) : viewBy.name === "Moments" || viewBy.name === "Topics" ? (
      <React.Fragment>
        {data.displayName.substring(0, 3) === "Pri" ? (
          <i className="bi-lock-fill m-r-6" />
        ) : (
          <i className="bi-people m-r-6" />
        )}
        <span>{`${data.displayName.substring(4)}`}</span>
        <span className="boxLabelAI m-l-4 bg-primary tx-white">
          {data.additionalProperties["IsExactMatch"] === "true"
            ? "EXACT"
            : "EXTENDED"}
        </span>
      </React.Fragment>
    ) : (
      <span>{`${data.displayName}`}</span>
    );

    return (
      <td className="mx-th-tag">
        <div>{label}</div>
      </td>
    );
  };
  const CustomTotalCallsPropCell = (props: CustomGridCellProps) => {
    const data = props.dataItem;

    type SubKey =
      | "ExactMatchTopic"
      | "LowRelevanceTopic"
      | "MediumRelevanceTopic"
      | "HighRelevanceTopic";
    const order: Record<SubKey, number> = {
      ExactMatchTopic: 0,
      LowRelevanceTopic: 1,
      MediumRelevanceTopic: 2,
      HighRelevanceTopic: 3,
    };
    if (data.subKeyCallsCnt) {
      data.subKeyCallsCnt = data.subKeyCallsCnt.slice().sort((a, b) => {
        const orderA = order[a.key as SubKey] ?? 4;
        const orderB = order[b.key as SubKey] ?? 4;
        return orderA - orderB;
      });
    }

    const onCallsClick = (subKeyLabel?: string) => {
      let tinyUser: TinyUser | undefined = undefined;
      if (viewBy.name === "Users" || viewBy.name == "Evaluators") {
        try {
          tinyUser = JSON.parse(data.displayName);
        } catch (err) {}
      }
      const name = concatUsername(
        tinyUser?.firstName ? tinyUser.firstName : "",
        tinyUser?.lastName ? tinyUser?.lastName : ""
      ).trim();
      onViewByCallCountClickHandler(
        viewBy.name,
        data.name,
        subKeyLabel,
        tinyUser
          ? name.length > 0
            ? name
            : tinyUser.email
          : viewBy.name === "Topics"
          ? data.displayName.substring(4)
          : data.displayName
      );
    };

    const customTopicTitle = (key: string) => {
      switch (key) {
        case "LowRelevanceTopic":
          return "Low Relevance";
        case "MediumRelevanceTopic":
          return "Medium Relevance";
        case "HighRelevanceTopic":
          return "High Relevance";
        case "ExactMatchTopic":
          return "Exact Match";
        default:
          return key;
      }
    };

    return (
      <td className="mx-th-tag">
        <div className="mx-td-spn">
          {data.subKeyCallsCnt.length > 0 ? (
            data.subKeyCallsCnt.map((c, idx) => {
              return (
                <div
                  key={idx}
                  className={`keywordTrk m-b-1 fs-15 cursor-pointer w-auto ${
                    c.key === "HighRelevanceTopic"
                      ? "bg-greenDLL"
                      : c.key === "LowRelevanceTopic"
                      ? "bg-yellowDLL"
                      : c.key === "ExactMatchTopic"
                      ? "bg-greenDarkDLL"
                      : ""
                  }`}
                  title={trans.fetchLabelKeyTranslation(
                    c.key,
                    customTopicTitle(c.key)
                  )}
                  style={{ color: "#1E6570" }}
                  onClick={(e) => onCallsClick(c.key)}
                >
                  {c.count}
                </div>
              );
            })
          ) : (
            <span
              className="cursor-pointer"
              style={{ color: "#259fb2" }}
              onClick={(e) => onCallsClick()}
            >
              {data.totalCallsCnt}
            </span>
          )}
        </div>
      </td>
    );
  };

  const CustomEvaluatedDurationPropCell = (props: CustomGridCellProps) => {
    const data = props.dataItem;

    return (
      <td className="mx-th-tag">
        <div className="mx-td-spn">
          <span style={{ color: "#259fb2" }}>{data.duration}</span>
        </div>
      </td>
    );
  };

  function convertMinutesToHours(minutes: number) {
    var hours = Math.floor(minutes / 60);
    var remainingMinutes = minutes % 60;

    if (hours === 0) {
      return `${remainingMinutes} mins`;
    } else if (remainingMinutes === 0) {
      return `${hours} hrs`;
    } else {
      return `${hours} hrs ${remainingMinutes} mins`;
    }
  }

  const CustomAverageScorePropCell = (props: CustomGridCellProps) => {
    const data = props.dataItem;

    return (
      <td className="mx-th-tag">
        <div className="mx-td-spn">
          <span style={{ color: "#259fb2" }}>{data.averageScore}</span>
        </div>
      </td>
    );
  };

  const CustomTotalEvaluatedCallsPropCell = (props: CustomGridCellProps) => {
    const data = props.dataItem;
    return (
      <td className="mx-th-tag">
        <div className="mx-td-spn">
          <span style={{ color: "#259fb2" }}>
            {
              data.evaluation?.metricKeyCounts.find(
                (metric) => metric.metricKey === "Total"
              )?.totalCount
            }
          </span>
        </div>
      </td>
    );
  };

  const CustomTotalEvaluatedDurationPropCell = (props: CustomGridCellProps) => {
    const data = props.dataItem;
    return (
      <td className="mx-th-tag">
        <div className="mx-td-spn">
          <span style={{ color: "#259fb2" }}>
            {formatTotalEvaluatedDuration(
              Number(
                data.evaluation?.metricKeyCounts.find(
                  (metric) => metric.metricKey === "Total"
                )?.additionalProperties.EvaluatedDuration
              )
            )}
          </span>
        </div>
      </td>
    );
  };

  const formatTotalEvaluatedDuration = (seconds: number) => {
    if (seconds < 60) {
      return `${seconds} sec`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes} mins ${remainingSeconds} sec`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const remainingMinutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = seconds % 60;
      return `${hours} hrs ${remainingMinutes} mins ${remainingSeconds} sec`;
    }
  };

  const CustomTotalAverageEvaluatedCallsScorePropCell = (
    props: CustomGridCellProps
  ) => {
    const data = props.dataItem;
    return (
      <td className="mx-th-tag">
        <div className="mx-td-spn">
          <span style={{ color: "#259fb2" }}>
            {Number(
              data.evaluation?.metricKeyCounts.find(
                (metric) => metric.metricKey === "Total"
              )?.additionalProperties.AverageScore
            ).toFixed(2)}
          </span>
        </div>
      </td>
    );
  };

  const CustomTotalDifferentEvaluatedCallsCell = (
    props: CustomGridCellProps
  ) => {
    const evaluationMetrics = props.dataItem.evaluation;

    const EvaluationBadge: React.FC<{ evaluation: string; cnt: number }> = ({
      evaluation,
      cnt,
    }) => {
      const cls =
        evaluation === "Good"
          ? "success"
          : evaluation === "Poor"
          ? "danger"
          : "warning";
      return (
        <div
          className="keywordTrk"
          style={{
            border: "2px solid rgba(0,0,0,.08)",
            background: "transparent",
          }}
        >
          <div className="tblUsr float-left">
            <div className="topUsrAreaPic">
              <div
                className="topUsrAreaPic-i"
                style={{ padding: "1px 1px 1px 0" }}
              >
                <div
                  className="contact-list-icon"
                  title={evaluation}
                  style={{ height: "22px", width: "22px", border: "none" }}
                >
                  <i
                    className={`bi bi-hand-thumbs-up-fill fs-18 text-${cls}`}
                  ></i>
                </div>
              </div>
            </div>
          </div>
          <span
            className="numCount"
            title={trans.fetchLabelKeyTranslation("TextCalls", "Calls")}
            style={{ color: "rgba(0,0,0,.4)", padding: "0", cursor: "default" }}
          >
            {cnt}
          </span>
        </div>
      );
    };

    return (
      <td className="mx-th-tag-notMove mxLabel">
        <div className="mx-td-spn">
          <div className="keywordRow">
            {evaluationMetrics &&
              evaluationMetrics.metricKeyCounts.map((m, idx) => {
                if (
                  m.metricKey === "Good" ||
                  m.metricKey === "Average" ||
                  m.metricKey === "Poor"
                ) {
                  return (
                    <EvaluationBadge
                      key={idx}
                      evaluation={m.metricKey}
                      cnt={m.totalCount}
                    />
                  );
                } else {
                  return null;
                }
              })}
          </div>
        </div>
      </td>
    );
  };

  const customHeaderCellDuration = (props: any) => {
    return (
      <div className="mx-th-tag">
        <div
          className="mx-td-spn line-height-2"
          style={{
            wordBreak: "break-word",
            maxWidth: "95%",
            color: "rgba(0, 0, 0, 0.35)",
            cursor: "default",
          }}
        >
          {props.title}
        </div>
      </div>
    );
  };

  const NotClickableHeader = (props: any) => {
    return (
      <div className="mx-th-tag">
        <div
          className="mx-td-spn line-height-2"
          style={{
            wordBreak: "break-word",
            maxWidth: "95%",
            color: "rgba(0, 0, 0, 0.35)",
            cursor: "default",
          }}
        >
          {props.title}
        </div>
      </div>
    );
  };

  const InsightViewByGridForMobileView = (
    mobileViewProps: GridCellProps,
    columns: any
  ) => {
    return (
      <ErrorBoundary FallbackComponent={fallbackRender}><Suspense fallback={<div><Loader></Loader></div>}>
      <InsightViewByGridForMobile
        {...mobileViewProps}
        viewBy={viewBy}
        columns={columns}
        viewByFilterTblData={viewByFilterTblData}
        onViewByCallCountClickHandler={onViewByCallCountClickHandler}
      /></Suspense></ErrorBoundary>
    );
  };

  const defaultViewByCols: GridColumnProps[] = [
    {
      width: 0,
      field: "cellMobile",
      cell: InsightViewByGridForMobileView,
      sortable: false,
    },
    {
      field: "displayName",
      title: trans.fetchLabelKeyTranslation(
        insightKeys[viewBy.displayName as keyof InsightKeysData],
        viewBy.displayName
      ),
      cell: CustomViewByHeaderCell,
      sortable: true,
    },
    {
      field: "totalCalls",
      title: trans.fetchLabelKeyTranslation("TotalCallsText", "Total Calls"),
      cell: CustomTotalCallsPropCell,
      width: 75,
      sortable: false,
    },
  ];

  const [sort, setSort] = React.useState([] as SortDescriptor[]);
  const [data, setData] = useState<AggregateData[]>([]);
  const [columns, setColumns] = useState<GridColumnProps[]>(defaultViewByCols);

  const [page, setPage] = useState<PageState>(initialDataState);
  const onPageChange = (event: GridPageChangeEvent) => {
    setPage(event.page);
  };
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  // useEffect(()=>{
  // },[columns.length])

  useEffect(() => {
    const buildViewTblData = async () => {
      setDataLoading(true);
      if (
        viewByFilterTblData &&
        viewByFilterTblData.rows.length > 0 &&
        viewByFilterTblData.rows[0].cells.length > 0
      ) {
        // set columns
        let foundUsersColumn = false;
        const cols: GridColumnProps[] = [];
        const row = viewByFilterTblData.rows[0];

        defaultViewByCols[0].headerCell = NotClickableHeader;
        defaultViewByCols[1].headerCell = NotClickableHeader;
        defaultViewByCols[2].headerCell = NotClickableHeader;

        row.cells.forEach((c, idx) => {
          if (idx > 0) {
            switch (c.metricKeyLabel) {
              case "Users": {
                cols.push({
                  field: "topUsers",
                  title: trans.fetchLabelKeyTranslation(
                    "TopUsersText",
                    "Top Users"
                  ),
                  cell: CustomTopUsersPropCell,
                  headerCell: NotClickableHeader,
                  sortable: false,
                });
                foundUsersColumn = true;
                break;
              }
              case "Duration": {
                cols.push({
                  field: "duration",
                  title: c.columnName,
                  cell: CustomEvaluatedDurationPropCell,
                  headerCell: NotClickableHeader,
                  sortable: false,
                });
                break;
              }
              case "AverageScore": {
                cols.push({
                  field: "duration",
                  title: c.columnName,
                  cell: CustomAverageScorePropCell,
                  headerCell: NotClickableHeader,
                  width: 200,
                  sortable: false,
                });
                break;
              }
              case "Sentiments": {
                cols.push({
                  field: "sentiments",
                  title: trans.fetchLabelKeyTranslation(
                    "SentimentsText",
                    "Sentiments"
                  ),
                  cell: CustomSentimentsCell,
                  headerCell: NotClickableHeader,
                  sortable: false,
                });
                break;
              }
              case "Topics": {
                cols.push({
                  field: "topTopics",
                  title: trans.fetchLabelKeyTranslation(
                    "MostDiscussedTopicsText",
                    "Most Discussed Topics"
                  ),
                  cell: CustomTopTopicsPropCell,
                  width: viewBy.name === "Users" ? 150 : "",
                  headerCell: customHeaderCellDuration,
                  sortable: false,
                });
                break;
              }
              case "Moments": {
                cols.push({
                  field: "topMoments",
                  title: c.columnName,
                  cell: CustomTopMomentsPropCell,
                  headerCell: NotClickableHeader,
                  sortable: false,
                });
                break;
              }
              case "Evaluation": {
                // Renaming "Total Calls" to "Total Evaluated Calls" only in case of "Evaluation"
                if (viewBy.name === "Evaluators") {
                  defaultViewByCols[2].field = "evaluation";
                  defaultViewByCols[2].title = trans.fetchLabelKeyTranslation(
                    "TotalEvaluatedCallsText",
                    "Total Evaluated Calls"
                  );
                  defaultViewByCols[2].width = 145;
                }
                // Adding column called "Total Evaluated Calls" only in case of "Users"
                else if (viewBy.name === "Users") {
                  cols.push({
                    field: "evaluation",
                    title: trans.fetchLabelKeyTranslation(
                      "TotalEvaluatedCallsText",
                      "Total Evaluated Calls"
                    ),
                    cell: CustomTotalEvaluatedCallsPropCell,
                    headerCell: NotClickableHeader,
                    width: 93,
                    sortable: false,
                  });
                }

                // cols.push({
                //   field: "evaluation",
                //   title: trans.fetchLabelKeyTranslation(
                //     "TotalEvaluatedDurationText",
                //     "Total Evaluated Duration"
                //   ),
                //   cell: CustomTotalEvaluatedDurationPropCell,
                //   headerCell: NotClickableHeader,
                //   width: 145,
                //   sortable: false,
                // });

                cols.push({
                  field: "evaluation",
                  title: trans.fetchLabelKeyTranslation(
                    "AvgScoreText",
                    "Avg. Score %"
                  ),
                  cell: CustomTotalAverageEvaluatedCallsScorePropCell,
                  headerCell: NotClickableHeader,
                  width: 90,
                  sortable: false,
                });

                cols.push({
                  field: "evaluation",
                  title: trans.fetchLabelKeyTranslation(
                    "EvaluationText",
                    "Evaluation"
                  ),
                  cell: CustomTotalDifferentEvaluatedCallsCell,
                  headerCell: NotClickableHeader,
                  width: 170,
                  sortable: false,
                });
                break;
              }
            }
          }
        });

        defaultViewByCols[0].cell = (props) =>
          InsightViewByGridForMobileView(props, [
            defaultViewByCols[0],
            defaultViewByCols[1],
            defaultViewByCols[2],
            ...cols,
          ]);

        setColumns([
          defaultViewByCols[0],
          defaultViewByCols[1],
          defaultViewByCols[2],
          ...cols,
        ]);
        let uniqueLoginUserIds: number[] = [];
        if (foundUsersColumn) {
          viewByFilterTblData.rows.forEach((r) => {
            r.cells.forEach((c) => {
              if (c.metricKeyLabel === "Users") {
                c.metricKeyCounts.forEach((kc) => {
                  if (!uniqueLoginUserIds.includes(Number(kc.metricKey))) {
                    uniqueLoginUserIds.push(Number(kc.metricKey));
                  }
                });
              }
            });
          });
        } else if (viewBy.name === "Users" || viewBy.name === "Evaluators") {
          viewByFilterTblData.rows.forEach((r) => {
            const id = r.cells[0].metricKeyCounts[0].metricKey;
            if (!uniqueLoginUserIds.includes(Number(id))) {
              uniqueLoginUserIds.push(Number(id));
            }
          });
        }

        let tinyUsers: TinyUser[] = [];
        let error = false;
        try {
          const ids: GetTinyUsersDto = {
            userIds:
              viewBy.name === "Users" || foundUsersColumn
                ? uniqueLoginUserIds
                : [],
            loginUserIds:
              viewBy.name !== "Users" && !foundUsersColumn
                ? uniqueLoginUserIds
                : [],
          };
          const res = await usersService.getTinyUsers(ids);
          tinyUsers = res;
        } catch (err) {
          console.error(err);
          error = true;
        }

        // set data
        const updatedData: AggregateData[] = [];
        viewByFilterTblData.rows.map((r) => {
          const dataRow: AggregateData = {
            name: "",
            displayName: "N/A",
            totalCallsCnt: 0,
            subKeyCallsCnt: [],
            additionalProperties: {},
          };
          const cell = r.cells[0];
          dataRow.name = cell.metricKeyCounts[0].metricKey;
          if (viewBy.name === "Users") {
            dataRow.displayName = JSON.stringify(
              tinyUsers.find(
                (x) => x.id === Number(cell.metricKeyCounts[0].metricKey)
              )
            );
          } else if (viewBy.name === "Evaluators") {
            let tinyUser : TinyUser|undefined = tinyUsers.find(
                (x) =>
                  x.loginUserId === Number(cell.metricKeyCounts[0].metricKey)
              );
            if(tinyUser?.accountTypeId === 1){
              tinyUser.firstName="Auto QA";
              tinyUser.lastName=""
              tinyUser.email=""
              tinyUser.phoneNo=""
            }
            dataRow.displayName = JSON.stringify(tinyUser);
          } else {
            const metricKeyObj = processMetricKeyForDisplay(
              viewBy.name,
              cell.metricKeyCounts[0].metricKey,
              true
            );
            let translatedKey = trans.fetchLabelKeyTranslation(
              metricKeyObj.localizationKey,
              metricKeyObj.title
            );
            translatedKey = metricKeyObj.trim
              ? translatedKey.substring(0, 3)
              : translatedKey;
            dataRow.displayName = translatedKey;
          }

          Object.keys(cell.metricKeyCounts[0].additionalProperties).forEach(
            (a) => {
              dataRow.additionalProperties[a] =
                cell.metricKeyCounts[0].additionalProperties[a];
            }
          );

          dataRow.subKeyCallsCnt = [];
          if (cell.metricKeyCounts[0].subKeyCounts.length > 0) {
            cell.metricKeyCounts[0].subKeyCounts.forEach((c) => {
              dataRow.subKeyCallsCnt?.push(c);
            });
          }
          dataRow.totalCallsCnt = cell.metricKeyCounts[0].totalCount;
          if (dataRow.totalCallsCnt > 0) {
            for (let i = 1; i < r.cells.length; i++) {
              switch (r.cells[i].metricKeyLabel) {
                case "Users": {
                  const userCell: UsersInsightMetricCell = {
                    columnName: r.cells[i].columnName,
                    metricKeyLabel: r.cells[i].metricKeyLabel,
                    metricKeyCounts: [],
                  };
                  if (!error) {
                    userCell.metricKeyCounts = r.cells[i].metricKeyCounts.map(
                      (c) => {
                        return {
                          metricKey:
                            tinyUsers.find(
                              (x) => x.id === Number(c.metricKey)
                            ) ?? tinyUsers[0],
                          count: c.totalCount,
                        };
                      }
                    );
                  }
                  dataRow.topUsers = userCell;
                  break;
                }
                case "Duration": {
                  dataRow.duration = convertMinutesToHours(
                    r.cells[i]?.metricKeyCounts[0]?.totalCount
                  );
                  break;
                }
                case "AverageScore": {
                  dataRow.averageScore =
                    r.cells[i]?.metricKeyCounts[0]?.totalCount + "%";
                  break;
                }
                case "Sentiments": {
                  dataRow.sentiments = r.cells[i];
                  break;
                }
                case "Topics": {
                  dataRow.topTopics = r.cells[i];
                  break;
                }
                case "Moments": {
                  dataRow.topMoments = r.cells[i];
                  break;
                }
                case "Evaluation": {
                  dataRow.evaluation = r.cells[i];
                }
              }
            }
            updatedData.push(dataRow);
          }
        });

        let sortedData: AggregateData[] = [];
        if (viewBy.name === "Evaluators" || viewBy.name === "Users") {
          const tempSort: SortDescriptor[] = [
            { field: "displayName", dir: "asc" },
          ];
          let findAIEvaluatorItem : AggregateData|undefined;
          const filteredUpdatedData = updatedData.filter((item:AggregateData)=>{
            const parsedItem = JSON.parse(item.displayName);
            if(parsedItem.accountTypeId===1){
              findAIEvaluatorItem = item;
              return false;
            }
            return true;
          })
          sortedData = sortDataOnDisplayName(tempSort, filteredUpdatedData);
          if(findAIEvaluatorItem)sortedData.unshift(findAIEvaluatorItem)
          setSort(tempSort);
          setData(sortedData);
        } else {
          setSort([]); // if we go to tab other than Users/Evaluators, the sorting arrow on the column should get removed
          setData(updatedData);
        }
      } else {
        setData([]);
        setSort([]);
      }
      setDataLoading(false);
    };

    if (viewByFilterTblData) {
      buildViewTblData();
    }
  }, [viewByFilterTblData, trans.translationsLoading]);

  

  const sortDataOnDisplayName = (
    tempSort: SortDescriptor[],
    data: AggregateData[]
  ) => {
    const sortedData = data.sort((a, b) => {
      // Parse displayName string into objects
      const displayNameA = JSON.parse(a.displayName);
      const displayNameB = JSON.parse(b.displayName);

      // Access firstName property from parsed objects
      const firstNameA = displayNameA.firstName ?? displayNameA.email;
      const firstNameB = displayNameB.firstName ?? displayNameB.email;

      // Compare first names for sorting
      if (tempSort[0].dir === "asc") {
        return firstNameA?.localeCompare(firstNameB);
      } else if (tempSort[0].dir === "desc") {
        return firstNameB?.localeCompare(firstNameA);
      }
      return firstNameA.localeCompare(firstNameB);
    });
    return sortedData;
  };

  return (
    <React.Fragment>
      <div className="row p-t-10 p-r-20 p-l-20 p-b-1">
        <div className="col-md-12">
          <div className="bg-white d-flex justify-content-between">
            <span className="font-weight-semi d-flex align-items-center">
              {`${trans.fetchLabelKeyTranslation(
                "ViewByText",
                "View by"
              )} ${trans.fetchLabelKeyTranslation(
                insightKeys[viewBy.displayName as keyof InsightKeysData],
                viewBy.displayName
              )}`}
            </span>
          </div>
        </div>
      </div>
      <div className="row p-t-10">
        <div className="col-md-12">
          <div className="maxTableCol tableList listViewByUser">
            <div className="table-container table-responsive table-overflow-hedden borderLeftSecondChild table-mobile">
              <Grid
                data={data?.slice(page.skip, page.take + page.skip)}
                skip={page.skip}
                take={page.take}
                total={data?.length}
                pageable={{ buttonCount: 4, pageSizes: true }}
                onPageChange={onPageChange}
                sortable={true}
                sort={sort}
                onSortChange={(e: GridSortChangeEvent) => {
                  if (viewBy.name === "Users" || viewBy.name === "Evaluators") {
                    let tempSort = e.sort;
                    if (e.sort.length === 0 && sort.length !== 0) {
                      tempSort = sort.map((s) => {
                        return { field: s.field, dir: "asc" };
                      });
                    }
                    let findAIEvaluatorItem : AggregateData|undefined;
                    const filteredUpdatedData = data.filter((item:AggregateData)=>{
                      const parsedItem = JSON.parse(item.displayName);
                      if(parsedItem.accountTypeId===1){
                        findAIEvaluatorItem = item;
                        return false;
                      }
                      return true;
                    })
                    let sortedData = sortDataOnDisplayName(tempSort, filteredUpdatedData);
                    if(findAIEvaluatorItem)sortedData.unshift(findAIEvaluatorItem)
                    setData(sortedData);
                    setSort(tempSort);
                  }
                }}

                //resizable={true}
              >
                <GridNoRecords>
                  {!viewByFilterTblData && dataLoading && (
                    <Loader type={"infinite-spinner"} />
                  )}
                  {viewByFilterTblData &&
                    !dataLoading &&
                    !error &&
                    data &&
                    data.length === 0 && (
                      <span className="fs-15">
                        {trans.fetchLabelKeyTranslation(
                          "MsgNoRecordfnd",
                          "No records matching with this filter!"
                        )}
                      </span>
                    )}
                  {error && (
                    <span className="fs-15">
                      <i className="bi bi-exclamation-triangle-fill tx-amber"></i>{" "}
                      {trans.fetchLabelKeyTranslation(
                        "FailedTextMsg",
                        "Uh Oh! Something Went Wrong. Please Try Again!"
                      )}
                    </span>
                  )}
                </GridNoRecords>
                {columns.map((column, idx) => {
                  return <GridColumn key={idx} {...column} />;
                })}
              </Grid>
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default InsightViewByGrid;
